// =============================================================================
// features/sales/agenda/AgendaFilters.tsx — Filtros por responsável, canal,
// campanha e situação (doc 26 §6.1).
//
// "Responsável" só aparece para quem tem `sales_appointments:manage` — um
// vendedor comum já abre filtrado em si mesmo pelo próprio backend (F33-S02:
// `restrictAgentId` no service), então o filtro seria decorativo pra ele.
// "Campanha" e "Situação" (dentro de Concluídos) filtram client-side — ver
// nota de cabeçalho em filters.ts.
// =============================================================================

import { CONTACT_CHANNELS } from '@elemento/shared-schemas';
import type { ContactChannel } from '@elemento/shared-schemas';
import * as React from 'react';

import { Select } from '../../../components/ui/Select';

import type { AgendaTab } from './buckets';
import { CAMPAIGN_FILTER_OPTIONS, SITUACAO_FILTER_OPTIONS } from './filters';
import type { CampaignFilter, SituacaoFilter } from './filters';
import { CHANNEL_LABELS } from './labels';

const CHANNEL_OPTIONS = [
  { value: 'all', label: 'Todos os canais' },
  ...CONTACT_CHANNELS.map((c: ContactChannel) => ({ value: c, label: CHANNEL_LABELS[c] })),
];

export interface AgendaFiltersProps {
  tab: AgendaTab;
  channel: ContactChannel | 'all';
  onChannelChange: (value: ContactChannel | 'all') => void;
  campaignFilter: CampaignFilter;
  onCampaignFilterChange: (value: CampaignFilter) => void;
  situacaoFilter: SituacaoFilter;
  onSituacaoFilterChange: (value: SituacaoFilter) => void;
  canFilterByAgent: boolean;
  assignedAgentId: string | 'all';
  onAssignedAgentChange: (value: string | 'all') => void;
  agentOptions: { value: string; label: string }[];
}

export function AgendaFilters({
  tab,
  channel,
  onChannelChange,
  campaignFilter,
  onCampaignFilterChange,
  situacaoFilter,
  onSituacaoFilterChange,
  canFilterByAgent,
  assignedAgentId,
  onAssignedAgentChange,
  agentOptions,
}: AgendaFiltersProps): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-end gap-3">
      {canFilterByAgent && (
        <div className="w-48">
          <Select
            id="agenda-filter-responsavel"
            label="Responsável"
            value={assignedAgentId}
            onChange={(e) => onAssignedAgentChange(e.target.value)}
            options={[{ value: 'all', label: 'Todos' }, ...agentOptions]}
          />
        </div>
      )}

      <div className="w-44">
        <Select
          id="agenda-filter-canal"
          label="Canal"
          value={channel}
          onChange={(e) => onChannelChange(e.target.value as ContactChannel | 'all')}
          options={CHANNEL_OPTIONS}
        />
      </div>

      <div className="w-44">
        <Select
          id="agenda-filter-campanha"
          label="Campanha"
          value={campaignFilter}
          onChange={(e) => onCampaignFilterChange(e.target.value as CampaignFilter)}
          options={CAMPAIGN_FILTER_OPTIONS}
        />
      </div>

      {tab === 'concluidos' && (
        <div className="w-44">
          <Select
            id="agenda-filter-situacao"
            label="Situação"
            value={situacaoFilter}
            onChange={(e) => onSituacaoFilterChange(e.target.value as SituacaoFilter)}
            options={SITUACAO_FILTER_OPTIONS}
          />
        </div>
      )}
    </div>
  );
}
